import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// Components
import { Card, SMBSelect, Table } from '../../../../components';
import { Close, DeleteForever, Add, AccountTree } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Grid,
  MenuItem,
  Typography,
  Divider,
  TextField,
  ButtonGroup,
  Alert,
  AlertTitle,
  Box
} from '@mui/material';
import { INeighborhoodProjectManagers, IDropdownResponse } from '../../../../models';
import {
  getNeighborhoodProjectManagers,
  getAvailableProjectManagers,
  getProjectManagerNeighborhoodNames,
  addProjectManagerToNeighborhood,
  deleteProjectManagerNeighborhood
} from '../../../../fetch';

export const ProjectManagers = ({ neighborhoodId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedProjectManagerId, setSelectedProjectManagerId] = useState<string | number>('');
  const [selectedProjectManagerName, setSelectedProjectManagerName] = useState<string>('');
  const [projectManagerPriorityOrder, setProjectManagerPriorityOrder] = useState<string | number>(
    ''
  );
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const classes = useStyles();

  const formatDetails = (details: []) => {
    let neighborhoods = details.join(', ');

    return (
      <>
        {neighborhoods.length !== 0 ? (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Project Manager {selectedProjectManagerName} is currently assigned to:
            </AlertTitle>
            {neighborhoods}
          </Alert>
        ) : (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Project Manager {selectedProjectManagerName} is not assigned to any neighborhoods.
            </AlertTitle>
          </Alert>
        )}
      </>
    );
  };

  const {
    isLoading: isLoadingAvailableProjectManagers,
    data: availableProjectManagers,
    refetch: refetchAvailableProjectManagers
  } = useQuery<IDropdownResponse[], Error>(
    ['getAvailableProjectManagers'],
    () => getAvailableProjectManagers(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const {
    isLoading: isLoadingProjectManagerNeighborhoodNames,
    data: projectManagerNeighborhoodNames
  } = useQuery<any, Error>(
    ['getProjectManagerNeighborhoodName', selectedProjectManagerId],
    () => getProjectManagerNeighborhoodNames(selectedProjectManagerId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!selectedProjectManagerId
    }
  );

  const {
    isLoading: isLoadingNeighborhoodProjectManagers,
    data: neighborhoodProjectManagers,
    refetch: refetchNeighborhoodProjectManagers
  } = useQuery<INeighborhoodProjectManagers[], Error>(
    ['getNeighborhoodProjectManagers', neighborhoodId],
    () => getNeighborhoodProjectManagers(neighborhoodId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!neighborhoodId
    }
  );

  const handleDelete = async (projectManagerId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this project manager?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteProjectManagerNeighborhood(projectManagerId, neighborhoodId);
        refetchNeighborhoodProjectManagers();
        enqueueSnackbar('Project manager removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting supervisor.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Priority',
        accessor: 'priorityOrder',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INeighborhoodProjectManagers } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.projectManagerId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.projectManagerId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.projectManagerId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card title="Project Managers" icon={<AccountTree />}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <SMBSelect
            fullWidth
            disabled={isLoadingAvailableProjectManagers}
            name={`neighborhoodId`}
            label="Project Manager"
            variant="outlined"
            handleClear={() => setSelectedProjectManagerId('')}
            id={`neighborhoodId`}
            value={selectedProjectManagerId}
            onChange={e => {
              setSelectedProjectManagerId(e.target.value);
            }}
          >
            {!isLoadingAvailableProjectManagers && availableProjectManagers?.length === 0 && (
              <MenuItem disabled>No neighborhoods available</MenuItem>
            )}
            {!isLoadingAvailableProjectManagers &&
              availableProjectManagers?.map((record, index) => {
                return (
                  <MenuItem
                    key={`${index}`}
                    value={record.value}
                    onClick={() => {
                      setSelectedProjectManagerName(record.description);
                    }}
                  >
                    {record.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            type="number"
            fullWidth
            size="small"
            autoComplete="nope"
            label="Priority Order"
            name="priorityOrder"
            value={projectManagerPriorityOrder}
            onChange={e => {
              setProjectManagerPriorityOrder(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box mb={1}>
            <ButtonGroup>
              <Button
                className={classes.button}
                startIcon={<Add />}
                sx={{ minWidth: '14rem' }}
                color="primary"
                variant="contained"
                disabled={!selectedProjectManagerId || !projectManagerPriorityOrder}
                onClick={async () => {
                  try {
                    await addProjectManagerToNeighborhood(
                      neighborhoodId,
                      selectedProjectManagerId,
                      projectManagerPriorityOrder
                    );
                    refetchAvailableProjectManagers();
                    refetchNeighborhoodProjectManagers();
                    setSelectedProjectManagerId('');
                    setProjectManagerPriorityOrder('');
                    enqueueSnackbar('Project Manager added!', {
                      variant: 'success'
                    });
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? 'Error adding Project Manager.', {
                      variant: 'error'
                    });
                  }
                }}
              >
                Add
              </Button>
              <Button
                className={classes.button}
                startIcon={<Close />}
                sx={{ minWidth: '14rem' }}
                color="inherit"
                variant="contained"
                disabled={!selectedProjectManagerId}
                onClick={() => {
                  setSelectedProjectManagerId('');
                  setProjectManagerPriorityOrder('');
                }}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Box>
        </Grid>
      </Grid>
      {projectManagerNeighborhoodNames && !isLoadingProjectManagerNeighborhoodNames && (
        <Typography className={classes.roleDetails}>
          {formatDetails(projectManagerNeighborhoodNames)}
        </Typography>
      )}
      <Divider />
      <Table
        columns={columns}
        data={neighborhoodProjectManagers ?? []}
        hidePagination
        isLoading={isLoadingNeighborhoodProjectManagers}
      />
    </Card>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  button: {
    whiteSpace: 'nowrap'
  },
  buttonGroup: {
    marginTop: theme.spacing(1)
  },
  roleDetails: {
    paddingBottom: theme.spacing(1)
  },
  selectWrapper: {
    width: '50%',
    marginBottom: '12px',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  textfieldWrapper: {
    width: '50%',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  alertTitle: {
    fontWeight: 'bold'
  }
}));
